import {
    EzException,
    EzBadParamException,
    EzBadStateException
} from '/ezlibrary/exceptions/EzExceptions.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Import /ezlibrary/helpers/* individualy. Do not import from /ezlibrary/helpers/EzHelpers.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/
import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzJson } from '/ezlibrary/helpers/EzJson.js';
import { EzDomRect } from '/ezlibrary/ux/EzDomRect.js';

/**
 * @class
 * @description
 * EzClocker UX Helper Class
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzUx } from '/ezlibrary/ux/EzUx.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Do not create new instances of this class!
 * Access is via static methods and properties only
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzUx {
    /**
     * @public @readonly @property
     * Gets the name of the <html> element: 'html'
     *
     * @returns {string}
     */
    static get htmlTagName() {
        return 'html';
    }

    /**
     * @public @readonly @property
     * Gets the name of the <head> element: 'head'
     *
     * @returns {string}
     */
    static get headTagName() {
        return 'head';
    }

    /**
     * @public @readonly @property
     * Gets the name of the <title> element: 'title'
     *
     * @returns {string}
     */
    static get titleTagName() {
        return 'title';
    }

    /**
     * @public @readonly @property
     * Gets the name of the <body> element: 'body'
     *
     * @returns {string}
     */
    static get bodyTagName() {
        return 'body';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets constant css class name (from ez-text.css):  'ezText-underline-hide-alt-key-up'
     * @returns {string}
     */
    static get css_ezTextUnerlineHideAltKeyUp() {
        return 'ezText-underline-hide-alt-key-up';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets constant css class name (from ez-text.css): 'ezText-underline-show-alt-key-down'
     * @returns {string}
     */
    static get css_ezTextUnderlineShowAltKeyDown() {
        return 'ezText-underline-show-alt-key-down';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the reference to the browser's Window instance that contains the DOM document.
     *
     * Normally availabe from one or more of the following:
     * 1) globalThis.window
     * 1) window
     *
     * @returns {Window}
     */
    static get ezWindow() {
        return globalThis?.window || EzObject.assignOrNull(window);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the browser's Window's Navigator interface that represents the state and the identity of the user agent. It allows scripts to query it
     * and to register themselves to carry on some activities.
     *
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.navigator
     * 2) window.navigator
     * 3) globalThis.navigator
     * 4) navigator
     *
     * @returns {Navigator}
     */
    static get ezNavigator() {
        return EzUx.ezWindow?.navigator || globalThis?.navigator || EzObject.assignOrNull(navigator);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the reference to the browser Window's screen
     *
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.screen
     * 2) window.screen
     * 3) screen
     *
     * @returns {Screen}
     */
    static get ezScreen() {
        return EzUx.ezWindow?.screen || EzObject.assignOrNull(screen);
    }


    /**
     * @static
     * @public @readonly @property
     * Gets the reference to the browser's HTMLDocment
     *
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.document
     * 2) window.document
     * 3) globalThis.document
     * 4) document
     *
     * @returns {HTMLDocument}
     */
    static get ezDocument() {
        return EzUx.ezWindow?.document || globalThis?.document || EzObject.assignOrNull(document);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the browser's Window HTMLDocument's <head> element.
     *
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.document.head
     * 2) window.document.head
     * 3) globalThis.document.head
     * 4) document.head
     * ... is it ready ezHead? ... :( SHAME on you for thinking THAT was funny ...
     *
     * @returns {Element}
     */
    static get ezHead() {
        return EzObject.assignOrNull(EzUx.ezDocument?.head);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the browser's Window HTMLDocument's <head> element.
     *
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.document.title
     * 2) window.document.title
     * 3) globalThis.document.title
     * 4) document.title
     *
     * @returns {Element}
     */
    static get ezTitle() {
        return EzObject.assignOrNull(EzUx.ezDocument?.title);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the browser's Window HTMLDocument's <body> element.
     *
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.document.body
     * 2) window.document.body
     * 3) globalThis.document.body
     * 4) document.body
     *
     * @returns {Element}
     */
    static get ezBody() {
        return EzObject.assignOrNull(EzUx.ezDocument?.body);
    }

    /**
     * @static
     * @public @method
     * 1) Gets the instance of the element who's name equals the provided globalElementNameOrInstance string
     * 2) Gets the provided globalElementNameOrInstance if the provided globalElementNameOrInstance
     *    is an instance of a known global element.
     *
     * @returns {Element|null}
     *
     * @deprecated
     * Migrate to: EzUx.globalElement(globalElementNameOrInstance)
     */
    static ezGlobalElement(globalElementNameOrInstance) {
        return EzUx.globalElement(globalElementNameOrInstance);
    }

    /**
     * @static
     * @public @method
     * 1) If the provided globalElementNameOrInstance is a string and matches a known global HTML element,
     *    then the global HTML element referece is returned.
     * 2) If the provided globalElementNameOrInstance is a instance of an HTML element who's name matches a known global HTML element,
     *    then the glboal HTML element reference is returned.
     * 3) Otherwise, null is returned.
     *
     * @returns {Element|null}
     */
    static globalElement(globalHtmlElementNameOrGlobalHtmlElementRef) {
        if (!EzObject.isValid(globalHtmlElementNameOrGlobalHtmlElementRef)) {
            // No global element name or HTML element reference provided
            return null;
        }

        if (EzString.isString(globalHtmlElementNameOrGlobalHtmlElementRef)) {
            switch (globalHtmlElementNameOrGlobalHtmlElementRef.toLowerCase()) {
                case 'window':
                    return EzUx.ezWindow;
                case 'navigator':
                    return EzUx.ezNavigator;
                case 'screen':
                    return EzUx.ezScreen;
                case 'document':
                    return EzUx.ezDocument;
                case EzUx.bodyTagName:
                    return EzUx.ezBody;
                case EzUx.headTagName:
                    return EzUx.ezHead;
                case EzUx.titleTagName:
                    return EzUx.ezTitle;
                default:
                    return null;
            }
        }

        if (EzUx.ezWindow === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezWindow;
        }

        if (EzUx.ezNavigator === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezNavigator;
        }

        if (EzUx.ezScreen === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezScreen;
        }

        if (EzUx.ezDocument === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezDocument;
        }

        if (EzUx.ezBody === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezBody;
        }

        if (EzUx.ezHead === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezHead;
        }

        if (EzUx.ezTitle === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezTitle;
        }

        // Supported global element name or instance not found
        return null;
    }

    /**
     * @static
     * @public @method
     * Finds an HTML element by id, name, tag name, or class name.
     *
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezFindByElementOrId(elementOrId)
     *  globalThis.ezApi.ezclocker.ezElement(elementId)
     *  globalThis.ezApi.ezclocker.ezUi.ezId(id)
     *  globalThis.ezApi.ezclocker.ezUi.ezId$(selector)
     *  globalThis.ezApi.ezclocker.ezRef(elementRef)
     *
     * @param {string} ezElementQuery
     * Default: null
     * Supports the following types for the elementQuery param:
     *  1) An HTML element reference
     *  2) An HTML element id string
     *  3) A jQuery HTML element reference (include the array of elements)
     *  4) A HTML element jQueryId string (aka '#id')
     *  5) HTML tag name
     *  6) HTML class name
     *  5) A jQuery selector
     *
     * @returns {null|object}
     */
    static findElement(ezElementQuery = null) {
        if (!EzObject.isValid(ezElementQuery)) {
            // Undefined or null value for ezElementQuery, returning null result
            return null;
        }

        if (!EzString.isString(ezElementQuery)) {
            // Assuming to be an element reference
            return ezElementQuery;
        }

        // Find by global HTML element name or global HTML element reference,
        let htmlElement = EzUx.globalElement(ezElementQuery);

        if (EzObject.isValid(htmlElement)) {
            return htmlElement;
        }

        // Find by HTML element reference, or HTML element with matching id
        htmlElement = EzUx.findById(ezElementQuery);

        if (EzObject.isValid(htmlElement)) {
            return htmlElement;
        }

        // Find by HTML element's class name (returning first encountered only)
        let htmlElements = EzUx.findAllByClassName(ezElementQuery);

        if (0 !== htmlElements?.length) {
            return htmlElements[0];
        }

        // Find and return the first HTML element found via globalThis.document.getElementsByName(elementName)
        htmlElements = EzUx.findAllByName(ezElementQuery);

        if (0 !== htmlElements?.length) {
            return htmlElements.item(0);
        }

        // Find via jQuery id query
        let jQueryElementRef = EzUx.$id(ezElementQuery);

        if (EzObject.isValid(jQueryElementRef)) {
            return 0 !== jQueryElementRef?.length
                ? jQueryElementRef[0]
                : null;
        }

        // Finally, try and find by jQuery selector
        jQueryElementRef = EzUx.$(ezElementQuery);

        return EzObject.isValid(jQueryElementRef) && 0 !== jQueryElementRef?.length
            ? jQueryElementRef[0]
            : null;
    }

    /**
     * @static
     * @public @method
     * Finds the HTML element who's id matches the provided elementId
     * Makes the assumtion that all the HTML elements in the current document have unique ids and if the matching id is associated with more than one
     * HTML element only the first HTML element encountered is returned.
     * Returns null if no HTML element's id matches the provided elementId value (case sensitive)
     *
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezId(id) (for non-jQueryIds)
     *  globalThis.ezApi.ezclocker.ezElement(elementId) (for non-jQueryIds)
     *
     * @param {string} elementId
     *
     * @returns {null|object}
     */
    static findById(elementId) {
        if (!EzString.hasLength(elementId)) {
            return null;
        }

        const globalElement = EzUx.globalElement(elementId);

        if (EzObject.isValid(globalElement)) {
            return globalElement;
        }

        // If the elementOrId is an object, assume it is an HTML element and return it.
        // Otherwise, look up the HTML element by id and return it.
        return EzObject.assignOrNull(
            globalThis.document.getElementById(elementId));
    }

    /**
     * @public @method
     * Attempts to determine the jQuery ID reference for the provided elementRefOrId value.
     * Throws EzBadParamException if the jQueryId could not be determined from the provided parameter value.
     *
     * @param {object|string} elementId
     * Expecting the value to be one of the following:
     *  1) An HTML element reference
     *  2) An HTML element id string
     *  3) A jQuery element reference (could be array of jQuery found elements)
     *  4) A jQuery id string with format: `#${element_id}`
     *
     * @return {string}
     */
    static determineJQueryId(elementId = EzString.EMPTY) {
        if (!EzObject.isValid(elementId) || !EzString.hasLength(elementId)) {
            throw new EzBadParamException(
                { elementId },
                this,
                this.determineJQueryId,
                'A valid HTML element reference or string id of an existing HTML element is required.');
        }

        let jQueryId = elementId;

        if (!EzString.isString(jQueryId)) {
            if (0 < jQueryId?.length) {
                // Obtain the id of the first element that has an id
                for (let index = 0; index < jQueryId?.length; index++) {
                    if (EzString.hasLength(jQueryId[index]?.id)) {
                        jQueryId = jQueryId[index].id;

                        break;
                    }
                }
            } else {
                // Obtain the Id of the HTML element reference (if it has one)
                jQueryId = EzString.stringOrEmpty(jQueryId?.id);
            }
        }

        if (!EzString.hasLength(jQueryId)) {
            throw new EzBadParamException(
                { elementId },
                this,
                this.determineJQueryId,
                EzString.em`
                    Unable to determine the jQueryID from the provided elementRef_elementId_jQueryElementRef_jQueryId
                    param value due to the following error:
                    A valid HTML element reference, HTML element id, jQuery element reference, or jQuery element id selector is expected but was not found.
                    [elementId=${elementId}`);
        }

        return '#' === jQueryId[0]
            ? jQueryId
            : `#${jQueryId}`;
    }

    /**
     * @static
     * @public @method
     * Alias for EzUx.id$(elementId)
     * Alias for EzUx.$id(elementId)
     * Alias for EzUx.$(`#${elementId}`)
     *
     * Returns a jQuery reference to the element with the provided id
     *
     * @param {string} elementId
     *
     * @deprecated
     * Migrate to one of the following:
     *  1) EzUx.ezId(elementId)
     *  2) EzUx.id(elementId)
     *  1) EzUx.$id(elementRefOrIdOrJQueryId)
     *  2) EzUx.id(elementId)
     *  3) EzUx.$(jQuerySelector)
     */
    static findJQueryElement(elementId) {
        try {
            return $(EzUx.determineJQueryId(elementId));
        } catch (err) {
            globalThis?.console?.error(
                EzString.em`
                    Unable to find an HTML element that is associated with the provided elementRefOrIdOrJQueryId param value
                    due to the following error: ${err.message}. Will return a null result instead.
                    [elementRefOrIdOrJQueryId: ${EzJson.toJson(elementId)}]`);

            return null;
        }
    }

    /**
     * @static
     * @public @method
     * Alias for EzUx.findJQueryElement(jQuerySelector)
     * Alias for EzUx.$id(jQuerySelector)
     * Alias for EzUx.$(`#${elementId}`)
     *
     * @param {string} jQuerySelector
     * A valid jQuery selector value
     *
     * @returns {object|null}
     *
     * @deprecated
     * Migrate to one of the following:
     *  1) EzUx.$id(elementRefOrIdOrJQueryId)
     *  2) ExUx.$(jQuerySelector)
     */
    static id$(elementId) {
        return EzUx.findJQueryElement(elementId);
    }

    /**
     * @public @method
     * Alias for EzUx.findJQueryElement(id)
     * Alias for EzUx.id$(id)
     * Alias for EzUx.$(`#${elementId}`)
     *
     * Gets a jQuery HTML element reference associated with the provided elementRefOrIdOrJQueryId param value.
     *
     * Can replace:
     *  1) $(`#${elementId}) jQuery query for HTML element id
     *  1) globalThis.ezApi.ezclocker.ezUi.ezId(id)
     *  2) globalThis.ezApi.ezclocker.ezElement(elementId)
     *  3) globalThis.ezApi.ezId(id)
     *  4) globalThis.ezApi.ezId$(id)
     *
     * @param {string} elementId
     * Supports the following value types:
     *  1) HTML element reference
     *  2) HTML element id string
     *  3) jQuery HTML element reference
     *  4) HTML element jQueryId string (aka '#id')
     */
    static $id(elementId) {
        return EzUx.findJQueryElement(elementId);
    }

    /**
     * @static
     * @public @method
     * Alias for EzUx.findJQueryElement(id)
     * Alias for EzUx.id$(id)
     * Alias for EzUx.$id(id)
     *
     * Finds an element via jQuery selector
     *
     * Can replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezId$(selector) (for jQuery element references, jQuery ids and selectors)
     *  globalThis.ezApi.ezclocker.ezRef(elementRef) (for jQuery element references, jQuery ids and selectors)
     *  globalThis.ezApi.ezclocker.ezId$(selector)
     *  globalThis.ezApi.ezclocker.ezRef(elementRef) (for jQuery element references, jQuery ids and selectors)
     *  globalThis.ezApi.ezclocker.ezId$(selector)
     *  globalThis.ezApi.ez$(jQuerySelector)
     *
     * @returns {null|object}
     */
    static $(jQuerySelector) {
        if (!EzString.hasLength(jQuerySelector) || !EzObject.isValid(jQuerySelector)) {
            globalThis?.console?.error(
                EzString.em`
                    A valid jQuery selector value is expected for the parameter jQuerySelector in call to ${EzUx.$}.
                    Will return a null result instead.`);

            return null;
        }

        const jQueryElement = $(jQuerySelector);

        if (!EzObject.isValid(jQueryElement) || 0 !== jQueryElement?.length) {
            globalThis?.console?.error(
                EzString.em`
                    Unable to locate an HTML element for the provided jQuerySelector param
                    with value of ${EzJson.toJson(jQuerySelector)}
                    by using jQuery api call $(${jQuerySelector}).
                    Will return a null result instead.`);

            return null;
        }

        return jQueryElement;
    }

    /**
     * @static
     * @public @method
     * Gets the element assocaited with the provided elementId
     *
     * Alias to EzUx.id - available to make switching from EzUi to EzUx easier.
     * Alias short-hand alternative to EzUx.findById
     *
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezId(id)
     *  globalThis.ezApi.ezclocker.ezElement(elementId)
     *  globalThis.ezApi.ezId(id)
     *  globalThis.ezApi.ezId$(id)
     *
     * @param {string} elementId
     */
    static ezId(elementId) {
        return EzUx.findById(elementId);
    }

    /**
     * @static
     * @public @method
     * Gets the element assocaited with the provided elementId
     *
     * Alias to EzUx.ezId - available to make switching from EzUi to EzUx easier.
     * Alias Short-hand alternative to EzUx.findById
     *
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezId(id)
     *  globalThis.ezApi.ezclocker.ezElement(elementId)
     *  globalThis.ezApi.ezId(id)
     *  globalThis.ezApi.ezId$(id)
     *
     * @param {string} elementId
     */
    static id(elementId) {
        return EzUx.findById(elementId);
    }

    /**
     * @static
     * @public @method
     * Will return a live HTMLCollection of all elements which have all of the given class name(s).
     *
     * @param {string} classNames
     * A string representing the class name(s) to match; multiple class names are separated by whitespace.
     *
     * @returns {HTMLCollection}
     */
    static findAllByClassName(classNames) {
        if (!EzString.hasLength(classNames)) {
            return [];
        }

        return globalThis.document.getElementsByClassName(classNames);
    }

    /**
     * @static
     * @public @method
     * Will return a live HTMLCollection of found elements in the order they appear in the tree.
     *
     * @param {string} elementTagName
     * The value of the name attribute of the element(s) to find
     *
     * @returns {HTMLCollection}
     */
    static findAllByTagName(elementTagName) {
        if (!EzString.hasLength(elementTagName)) {
            return [];
        }

        return globalThis.document.getElementsByTagName(elementTagName);
    }

    /**
     * @static
     * @public @method
     * Will return a live NodeList collection of elements with a given name attribute in the document (if any)
     *
     * Important: A live NodeList connection will automatically update as new elements with the same name are added to, or removed from,
     * the document.
     *
     * @param {string} elementName
     *
     * @returns {NodeList}
     */
    static findAllByName(elementName) {
        if (!EzString.hasLength(elementName)) {
            return [];
        }

        return globalThis.document.getElementsByName(elementName);
    }

    /**
     * @public @method
     * Gets a list of elements that match the provided aria attribute's value.
     *
     * @param {string} ariaAttributeSuffix
     * String should not include the 'aria' prefix.
     * @param {string} value
     *
     * @returns {array}
     */
    static findByAriaAttributeSuffix(ariaAttributeSuffix, value) {
        if (!EzString.hasLength(ariaAttributeSuffix)) {
            throw new EzBadParamException(
                'ariaAttributeSuffix',
                EzUx,
                EzUx.findByAriaAttributeSuffix);
        }

        if (!EzString.isString(value)) {
            value = EzString.EMPTY;
        }

        const fullAriaAttributeName = ariaAttributeSuffix.startsWith('aria-')
            ? ariaAttributeSuffix
            : `aria-${ariaAttributeSuffix}`;

        return document.querySelectorAll(`[${fullAriaAttributeName}="${value}"]`);
    }

    /**
     * @static
     * @public @method
     * Determines if the element associated with the provided elementRef is visible (display != 'none')
     * @returns {boolean}
     */
    static isVisible(elementRef) {
        return EzObject.isValid(
            EzUx.getElementIfVisible(elementRef));
    }

    /**
     * @static
     * @public @method
     * Gets the HTML element associated with the provided elementRef if it is visible (aka display != 'none').
     * Otherwise, returns null.
     * @param {string} elementRef
     * @returns {null|object}
     */
    static getElementIfVisible(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!element) {
            return null;
        }

        return 'none' !== element.style.display
            ? element
            : null;
    }

    /**
     * @static
     * @public @method
     * Gets the HTML element associated with the provided elementRef if it is NOT visible (aka display:none).
     * Returns null if the element is visible OR does not exist.
     * @param {string} elementRef
     * @returns {null|object}
     */
    static getElementIfNotVisible(elementRef) {
        const element = EzUx.findElement(elementRef);

        return 'none' === element?.style?.display
            ? element
            : null;
    }

    /**
     * @static
     * @public @method
     * Removes all of the ezClocker legacy 'hidden' classes (if any) from the element.
     * If a legacy 'hide' class is detected on the element, the element's display is set to none.
     *
     * Legacy 'hide' classes that get removed:
     * 1) ezHiddenByDefault
     * 2) ezHidden
     * 3) ezHiddenDefault
     *
     * @param {string} elementRef
     * @returns {null|object}}
     * Returns a referece to the element if it exists, null otherwise.
     */
    static removeLegacyHiddenClasses(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            // No element to convert classes on
            return null;
        }

        if (element.classList.contains('ezHiddenByDefault')) {
            element.style.display = 'none';

            element.classList.remove('ezHiddenByDefault');
        }

        if (element.classList.contains('ezHidden')) {
            element.style.display = 'none';

            element.classList.remove('ezHidden');
        }

        if (element.classList.contains('ezHiddenDefault')) {
            element.style.display = 'none';

            element.classList.remove('ezHiddenDefault');
        }

        return element;
    }

    /**
     * @protected @method
     * Obtains the HTML element associated with the provided elementRef.
     * Throws Error if:
     * 1) Provided elementRef is undefined or null
     * 2) No html element exists for the elementRef
     *
     * @param {string} elementRef
     * ID of the HTML element
     * @param {undefined|null|string} pendingAction
     * Dfeault: EzUx.getElementForElementRef.toString();
     * @param {undefined|null|boolean} logOnly
     * Default: true
     *
     * @returns {object}
     * Returns the HTML element associated with the provided elementRef
     */
    static getElementForElementRef(elementRef = null, pendingAction = EzString.EMPTY, logOnly = true) {
        if (!EzString.hasLength(pendingAction)) {
            pendingAction = EzUx.getElementForElementRef.toString();
        }

        if (!EzString.hasLength(elementRef)) {
            const em = `Cannot execute '${pendingAction}' on HTML element: Element reference is undefined or null.`;

            globalThis?.console?.error(em);

            if (EzBoolean.booleanOrTrue(logOnly)) {
                throw new Error(em);
            }
        }

        const element = EzUx.findElement(elementRef);

        if (!element) {
            const em = `Cannot execute '${pendingAction}' on HTML element reference '${elementRef}': Element does not exist.`;

            globalThis?.console?.error(em);

            if (EzBoolean.booleanOrTrue(logOnly)) {
                throw new Error(em);
            }
        }

        return element;
    }

    /**
     * @static
     * @public @method
     * Applies the provdied Animate.style (animate.css) style to the HTML element associated with the provided elementRef.
     *
     * @param {string} elementRef
     * Id of the HTML element to apply the animate.css class to
     * @param {string} animateStyleCssClass
     */
    static animate(elementRef, animateStyleCssClass) {
        EzUx.getElementForElementRef(
            elementRef,
            'animate',
            true);

        if (!EzString.hasLength(animateStyleCssClass)) {
            globalThis?.console?.error(`Cannot execute 'animate' on HTML element reference ${elementRef}: Animation class is undefined or null.`);

            return;
        }
        if (!animateStyleCssClass.startsWith('animate__')) {
            globalThis?.console?.error(
                `Cannot animate element ${elementRef}: Animate.style animate.css classes must start with 'animate__'.` +
                'For more information see: https://animate.style/');

            return;
        }

        EzUx.addCssClass(
            elementRef,
            'animate__animated');

        EzUx.addCssClass(
            elementRef,
            animateStyleCssClass);
    }

    /**
     * @static
     * @public @method
     * Shows the HTML element asscoaited with the provdied elementRef using the fade-in anumation.
     *
     * @param {string} elementRef
     * Id of the HTML element to apply the animate.css class to
     */
    static fadeIn(elementRef) {
        EzUx.getElementForElementRef(
            elementRef,
            'fadeIn',
            false);

        EzUx.animate(
            elementRef,
            'animate__fadeIn');

        EzUx.show(elementRef);
    }

    /**
     * @static
     * @public @method
     * Shows the HTML element asscoaited with the provdied elementRef using the fade-in anumation.
     *
     * @param {string} elementRef
     * Id of the HTML element to apply the animate.css class to
     */
    static fadeOut(elementRef) {
        EzUx.getElementForElementRef(
            elementRef,
            'fadeOut',
            false);

        EzUx.animate(
            elementRef,
            'animate__fadeOut');

        EzUx.show(elementRef);
    }

    /**
     * @static
     * @public @method
     * Shows the element or element associated by the provided id
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezShowElement(elementOrId, optionalDisplayStyle)
     *  globalThis.ezApi.ezclocker.ezUi.ezShow(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezShow$(selector)
     *
     * @param {string} elementRef
     * @param {undefined|null|string} optionalDisplayStyle
     * Defaults:
     *  If element is a div then default is 'block'
     *  If element is a table then default is 'table'
     *  Otherwise, the default is 'initial'
     */
    static show(elementRef, optionalDisplayStyle = EzString.EMPTY) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            'show',
            true);

        const displayStyle = globalThis.getComputedStyle(element, null).display;

        if ('none' !== displayStyle) {
            // already visible
            return;
        }

        if (element.classList.contains('ezHiddenByDefault')) {
            element.style.display = 'none';

            element.classList.remove('ezHiddenByDefault');
        }

        if (element.classList.contains('ezHidden')) {
            element.style.display = 'none';

            element.classList.remove('ezHidden');
        }

        if (element.classList.contains('ezHiddenDefault')) {
            element.style.display = 'none';

            element.classList.remove('ezHiddenDefault');
        }

        if (EzString.hasLength(optionalDisplayStyle) && 'initial' !== optionalDisplayStyle) {
            element.style.display = optionalDisplayStyle;
        } else if (EzObject.hasProperty(element, 'ezDisplay') && EzString.hasLength(element.ezDisplay)) {
            element.style.display = element.ezDisplay;
        } else if ('DIV' === element.tagName.toUpperCase()) {
            element.style.display = 'block';
        } else if ('TABLE' === element.tagName.toUpperCase()) {
            element.style.display = 'table';
        } else if ('TD' === element.tagName.toUpperCase()) {
            element.style.display = 'table-cell';
        } else {
            element.style.display = 'unset';
        }

        // check what the display style is. if it is none, we set it to the user preferred display, as a last resort.
        if (EzString.hasLength(optionalDisplayStyle) && 'none' === globalThis?.getComputedStyle(element, null)?.display) {
            element.style.display = optionalDisplayStyle;
        }
    }

    /**
     * @static
     * @public @method
     * Hides the element or associated by the provided id
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezHideElement(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezHide$(selector)
     *
     * @param {string} elementRef
     */
    static hide(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            'hide');

        if (null == element) {
            return;
        }

        // getComputerStyle(...) is a global Javascript function available on globalThis
        const displayStyle = globalThis.getComputedStyle(element, null).display;

        if ('none' === displayStyle) {
            // Already hidden
            return;
        }

        element.ezDisplay = displayStyle;

        element.style.display = 'none';
    }

    /**
     * @static
     * @public @method
     * Gets the innerHtml content for the element associated with the provided elementRef
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezGetElementContent(elementOrId)
     * @param {string} elementRef
     * @returns {string}
     */
    static getContent(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot get element content for ${elementRef}: Element does not exist.`);

            return null;
        }

        return element.innerHTML;
    }

    /**
     * @static
     * @public @method
     * Sets the element's innerHtml
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetContent(id, innerHtmlContent)
     *  globalThis.ezApi.ezclocker.ezUi.ezContent(elementOrId, html)
     * @param {string} elementRef
     * @param {undefined|null|string} innerHTMLContent
     * Default: EzString.EMPTY
     */
    static setContent(elementRef, innerHTMLContent = EzString.EMPTY) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot get element content for ${elementRef}: Element does not exist.`);

            return;
        }

        element.innerHTML = EzString.stringOrEmpty(innerHTMLContent);
    }

    /**
     * @static
     * @public @method
     * Clears the element's innerHtml content by assigning EzString.EMPTY
     * Replaces:
     *  1) globalThis.ezApi.ezclocker.ezUi.ezClearContent(id, innerHtmlContent)
     *
     * @param {string} elementRef
     */
    static clearContent(elementRef) {
        return EzUx.setContent(elementRef, EzString.EMPTY);
    }

    /**
     * @static
     * @public @method
     * Prepends the provided prependHtmlContent to the beginning of the
     * content of the innerHTML
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezPrependContent(elementOrId, appendHtml)
     * @param {string} elementRef
     * @param {undefined|null|string} prependHtmlContent
     */
    static prependContent(elementRef, prependHtmlContent) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzString.hasLength(prependHtmlContent)) {
            return;
        }

        element.insertAdjacentHTML(
            'afterbegin',
            prependHtmlContent);
    }

    /**
     * @static
     * @public @method
     * Appends the provided appendHtml to the innerHTML property of the provided element or element associated with the
     * provided id.
     *
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezAppendContent(elementOrId, appendHtml)
     *  globalThis.ezApi.ezclocker.ezUi.ezAppend(id, html)
     *  globalThis.ezApi.ezclocker.ezUi.ezAppendHtml(elementOrId, appendHtml)
     *
     * @param {string} elementOrId
     * @param {undefined|null|string} appendHtmlContent
     */
    static appendContent(elementRef, appendHtmlContent = EzString.EMPTY) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzString.hasLength(appendHtmlContent)) {
            return;
        }

        element.insertAdjacentHTML(
            'beforeend',
            appendHtmlContent);

        /*
        element.innerHTML = EzHtml.build`
            ${EzString.stringOrEmpty(element?.innerHtml)}
            ${appendHtmlContent}`;
        */
    }

    /**
     * @static
     * @public @method
     * Appends the provided appendHtmlContent to innerHtml property of the element identified by the jquerySelector.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezAppendContent$
     *  globalThis.ezApi.ezclocker.ezUi.ezAppend$(selector, appendHtml)
     *  globalThis.ezApi.ezclocker.ezUi.ezAppendHtml$(selector, appendHtml)
     * @param {string} jquerySelector
     * @param {undefined|null|appendHtmlContent}
     */
    static appendContent$(jquerySelector, appendHtmlContent = EzString.EMPTY) {
        const element = EzUx.getElementForElementRef(
            jquerySelector,
            EzUx.setContent.toString(),
            true);

        if (EzString.hasLength(jquerySelector)) {
            globalThis?.console?.error('Cannot append content to element selected with an undefined or null jQuery selector.');
        }

        element.append(appendHtmlContent);
    }

    /**
     * @static
     * @public @method
     * Determines if the element with provided ID exists
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezElementExists(id)
     *  globalThis.ezApi.ezclocker.ezUi.ezDoesElementExist(elementOrId)
     * @returns {boolean}
     */
    static elementExists(elementRef) {
        const element = EzUx.findElement(elementRef);

        return EzObject.isValid(element);
    }

    /**
     * @static
     * @public @method
     * Determines if the element with provided ID exists
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezElementExists$(jquerySelector)
     * @param {string} jquerySelector
     * @returns {boolean}
     */
    static elementExists$(jquerySelector) {
        if (!EzString.hasLength(jquerySelector)) {
            return false;
        }

        const element = EzUx.$(jquerySelector);

        return EzObject.isValid(element) && 0 !== element.length;
    }

    /**
     * @static
     * @public @method
     * Sets focus on the element referenced by the jquery selector
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezFocusElement(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezFocusInput(id)
     *  globalThis.ezApi.ezclocker.ezUi.ezFocus(elementId)
     *
     * @param {selector} selector
     */
    static focus(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString());

        if (null != element) {
            element.focus();
        }
    }

    /**
     * @static
     * @public @method
     * Sets focus to the element referenced by the provided selector
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezFocus$(selector)
     *  globalThis.ezApi.ezclocker.ezUi.ezFocusInput$(selector)
     * @param {string} jquerySelector
     * @returns {null|object}
     */
    static focus$(jquerySelector) {
        if (!EzString.hasLength(jquerySelector)) {
            globalThis?.console?.error('Cannot focus an element selected by a null or undefiend jquery selector.');

            return null;
        }

        const element = EzUx.getElementForElementRef(
            jquerySelector,
            EzUx.setContent.toString(),
            true);

        return element.focus();
    }

    /**
     * @static
     * @public @method
     * Sets the input value
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetInputValue(elementOrId, value)
     * @param {string} elementRef
     * @param {undefined|null|number|string|boolean} value
     * If the value is undefined or null then the element.value is assigned null.
     */
    static setInputValue(elementRef, value) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        element.value = EzObject.assignOrNull(value);
    }

    /**
     * @static
     * @public @method
     * Sets the input value
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetInputValue(elementOrId, value)
     * @param {string} elementRef
     */
    static clearInputValue(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        element.value = EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Clears all the HTML input elements who's reference/id appear in the provided inputRefsArray.
     * @param {array} inputRefsArray
     */
    static clearAllInputValues(inputRefsArray = []) {
        if (!EzArray.hasLength(inputRefsArray)) {
            return;
        }

        inputRefsArray.forEach(
            (inputRef) => EzUx.clearInputValue(inputRef));
    }

    /**
     * @static
     * @public @method
     * Sets the input value for the item identified by the selector
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetInputValue$(selector, value)
     * @param {string} jquerySelector
     * @param {undefined|null|*} value
     */
    static setInputValue$(jquerySelect, value) {
        if (!EzString.hasLength(jquerySelect)) {
            globalThis?.console?.error('Cannot set the input using a null or undefiend jquery selector value.');

            return;
        }

        const element = EzUx.getElementForElementRef(
            jquerySelect,
            EzUx.setContent.toString(),
            true);

        if (EzObject.isValid(element)) {
            element.val(
                EzObject.assignOrNull(value));
        }
    }

    /**
     * @static
     * @public @method
     * Gets the value the HTML input element referenced by the provided elementRef
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezGetInputValue(elementOrId)
     * @param {string} elementRef
     * @returns {*}
     * Returns the value of the input assoicated with the provided elementRef
     */
    static getInputValue(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzObject.assignOrNull(element?.value);
    }

    /**
     * @static
     * @public @method
     * Determiens if the provied element refernce is the browser window.
     * @param {undefined|null|object} element
     * @returns {boolean}
     */
    static isElementWindow(element) {
        return EzObject.isValid(element) && '[object Window]' === EzObject.getObjectType(element);
    }

    /**
     * @static
     * @public @method
     * Gets the clientHeight of an element or the outerHeight of the window
     * @param {string} elementRef
     * @returns {number}
     */
    static clientHeight(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            return 0;
        }

        return element.constructor.name === EzUx.ezWindow?.constructor?.name
            ? window.outerHeight
            : element.clientHeight;
    }

    /**
     * @static
     * @public @method
     * Gets the clientWidth of an element or the outerWidth of the window
     * @param {string} elementRef
     * @returns {number}
     * Returns the element if found, or null.
     */
    static clientWidth(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            return 0;
        }

        return element.constructor.name === EzUx.ezWindow?.constructor?.name
            ? window.outerWidth
            : element.clientWidth;
    }

    /**
     * @static
     * @public @method
     * Gets an element's offsetHeight or the window's outerHeight value.
     * Returns zero if the element does not exist or of the value obtained from the window or element is not a number.
     * The inner height value is a sum of the element or window:
     *  1) Height
     *  2) Padding top value
     *  3) Padding bottom value
     *  4) Border top size value
     *  5) Border bottom size value
     * @param {string} elementRef
     * @returns {number}
     */
    static outerHeight(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzUx.isElementWindow(element)
            ? EzNumber.numberOrZero(window?.outerHeight)
            : EzNumber.numberOrZero(element?.offsetHeight);
    }

    /**
     * @static
     * @public @method
     * Gets an element's offsetWidth or the window's outerWidth value.
     * Returns zero if the element does not exist or of the value obtained from the window or element is not a number.
     * The value returned is the sum of the following element or window properties:
     *  1) Element height (without padding or border)
     *  2) Padding left value
     *  3) Padding right value
     *  4) Border left size value
     *  5) Border right size value
     * @param {string} elementRef
     * @returns {number}
     */
    static outerWidth(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzUx.isElementWindow(element)
            ? EzNumber.numberOrZero(window?.outerWidth)
            : EzNumber.numberOrZero(element?.offsetWidth);
    }

    /**
     * @static
     * @public @method
     * Gets an element's clientHeight or the window's innerHeight value.
     * Returns zero if the element/window does not exist or of the value obtained from the window or element is not a number.
     * The value returned is the height of the element or window without padding or border.
     * @param {string} elementRef
     * @returns {number}
     */
    static innerHeight(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzUx.isElementWindow(element)
            ? EzNumber.numberOrZero(window.innerHeight)
            : EzNumber.numberOrZero(element.clientHeight);
    }

    /**
     * @static
     * @public @method
     * Gets an element's clientWidth or the window's innerWidth value.
     * Returns zero if the element/window does not exist or of the value obtained from the window or element is not a number.
     * The value returned is the width of the element or window without padding or border.
     * @param {string} elementRef
     * @returns {number}
     */
    static innerWidth(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzUx.isElementWindow(element)
            ? EzNumber.numberOrZero(window.innerWidth)
            : EzNumber.numberOrZero(element.clientWidth);
    }

    /**
     * @static
     * @public @method
     * Gets an EzDomRect with the DOMRect in  formation for the provided element or id.
     *
     * @param {string} elementRef
     *
     * @returns {EzDomRect}
     */
    static elementClientRect(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        const domRect = element.getBoundingClientRect();

        return EzObject.isValid(domRect)
            ? new EzDomRect(domRect)
            : new EzDomRect(null, 0, 0, 0, 0, 0, 0, 0, 0);
    }

    /**
     * @public @method
     * Sets the element's CSS height value
     *
     * Replaces:
     *  1) globalThis.ezapi.ezclocker.ezUi.ezGetElementHeight(elementOrId)
     *
     * @param {string} elementRef
     */
    static getHeight(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            false);

        return EzObject.assignOrDefault(element?.style?.height, 0);
    }

    /**
     * @public @method
     * Sets the element's CSS height value
     *
     * Replaces:
     *  1) globalThis.ezapi.ezclocker.ezUi.ezSetElementHeight(elementOrId, height)
     *
     * @param {string} elementRef
     * @param {number|string} cssHeight
     */
    static setHeight(elementRef, cssHeight) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        element.style.height = EzNumber.isNumber(cssHeight)
            ? `${cssHeight}px`
            : cssHeight;
    }

    /**
     * @static
     * @public @method
     * Gets the element's offset top value
     *
     * @param {string} elementRef
     *
     * @return {number}
     */
    static offsetTop(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.error(`Cannot enable element element ${elementRef}: Element does not exist.`);

            return 0;
        }

        return element.offsetTop;
    }

    /**
     * @static
     * @public @method
     * Enables the element
     *
     * @param {string} elementRef
     */
    static enable(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.error(`Cannot enable element element ${elementRef}: Element does not exist.`);

            return;
        }

        element.disabled = false;
    }

    /**
     * @static
     * @public @method
     * Enables all the element references/ids in the elementRefArray param.
     *
     * @param {undefined|null|array} elementRefArray
     * Default: []
     */
    static enableAll(elementRefArray = []) {
        if (!EzArray.hasLength(elementRefArray)) {
            return;
        }

        elementRefArray.forEach(
            (elementRef) => EzUx.enable(elementRef));
    }

    /**
     * @static
     * @public @method
     * Disables the element
     *
     * @param {string} elementRef
     */
    static disable(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.error(`Cannot disable element ${elementRef}: Element does not exist.`);

            return;
        }

        element.disabled = true;
    }

    /**
     * @static
     * @public @method
     * Disables all the element references/ids in the elementRefArray param.
     *
     * @param {undefined|null|array} elementRefArray
     * Default: []
     */
    static disableAll(elementRefArray = []) {
        if (!EzArray.hasLength(elementRefArray)) {
            return;
        }

        elementRefArray.forEach(
            (elementRef) => EzUx.disable(elementRef));
    }

    /**
     * @static
     * @public @method
     * Determines if the elementRef is an existing element and is enabled.
     *
     * @param {string} elementRef
     */
    static isEnabled(elementRef) {
        return EzBoolean.isFalse(
            EzUx.isDisabled(elementRef));
    }

    /**
     * @static
     * @public @method
     * Determines if the elementRef is an existing element and is disabled.
     *
     * @param {string} elementRef
     */
    static isDisabled(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.error(`Cannot determine if element ${elementRef} is enabled or disabled: Element does not exist.`);

            return false;
        }

        return EzBoolean.isTrue(element?.disabled);
    }

    /**
     * @static
     * @public @method
     * Inserts the provided html by:
     *  1) Creating a <span> node with the provided wrapperId
     *  2) Assigning the provided html to the <span> node's innerHTML
     *  3) Inserting the <span> node before the element with the provided elementOrId.
     *
     * @param {string} elementRef
     * @param {undefined|null|string} html
     * Default is: empty string
     * @param {undefined|null|string} wrapperId
     * Default is: 'EzInsertedElementWrapperSpan'
     */
    static insertBeforeElement(elementRef, html, wrapperId) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.error(`Cannot insert html before element ${elementRef}: Element does not exist.`);

            return;
        }

        const spanNode = EzUx.ezDocument.createElement('div');

        spanNode.style.display = 'contents';

        spanNode.id = EzString.hasLength(wrapperId)
            ? wrapperId
            : 'EzInsertedElementWrapperSpan';

        spanNode.innerHTML = EzString.stringOrEmpty(html);

        element.parentNode.insertBefore(spanNode, element);
    }

    /**
     * @static
     * @public @method
     *
     * Sets the selected property of the element associated with the elementRef to true.
     * @param {string} elementRef
     */
    static select(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.error(`Cannot select element ${elementRef}: Element does not exist.`);

            return;
        }

        elementRef.selected = true;
    }

    /**
     * @static
     * @public @method
     *
     * Sets the selected property of the element associated with the elementRef to false.
     * @param {string} elementRef
     */
    static unselect(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.error(`Cannot select element ${elementRef}: Element does not exist.`);

            return;
        }

        element.selected = false;
    }

    /**
     * @static
     * @public @method
     * Returns if the element associated with the elementRef has it's selected property set to true.
     *
     * @param {string} elementRef
     *
     * @returns {boolean}
     */
    static isSelected(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.error(`Cannot select element ${elementRef}: Element does not exist.`);

            return false;
        }

        return EzBoolean.isTrue(element?.selected);
    }

    /**
     * @static
     * @public @method
     * Gets the element's dataset value for the provided dataSetKey for the element associated with the provided elementRef.
     * Can exclude the 'data-' prefix and/or send the dataSetKey as camel cased instead of with dashes.
     *
     * @param {string} elementRef
     * @param {string} dataSetKey
     *
     * @returns {*}
     */
    static getDatasetValue(elementRef, dataSetKey) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis.console.warn(`Cannot get element ${elementRef}'s dataset value for data set key "${dataSetKey}": Element does not exist.`);

            return undefined;
        }

        dataSetKey = 0 === dataSetKey.indexOf('data-')
            ? dataSetKey.substring(5).replace('-', EzString.EMPTY)
            : dataSetKey.replace('-', EzString.EMPTY);

        if (!EzString.hasLength(dataSetKey)) {
            throw new EzBadParamException(
                'dataSetKey',
                EzUx,
                EzUx.getDatasetValue);
        }

        return element?.dataset?.[dataSetKey];
    }

    /**
     * @static
     * @public @method
     * Gets the element's dataset value as a JSON object for the provided dataSetKey for the element associated with the provided elementRef.
     * Can exclude the 'data-' prefix and/or send the dataSetKey as camel cased instead of with dashes.
     *
     * @param {string} elementRef
     * @param {string} dataSetKey
     *
     * @returns {object}
     */
    static getDatasetValueAsJsonObject(elementRef, dataSetKey) {
        const dataSetValue = EzUx.getDatasetValue(elementRef, dataSetKey);

        if (!EzString.hasLength(dataSetValue)) {
            throw new EzBadStateException(
                `Expected a valid, non-empty JSON string from element.dataset[${dataSetKey}]`,
                EzString.em`
                    However, the value of element.dataset[${dataSetKey}] is undefined, null, or an empty string.
                    [Dataset Value: ${EzObject.isValid(dataSetValue) ? dataSetValue.toString() : 'undefined or null'}]`,
                EzUx,
                EzUx.getDatasetValueAsJsonObject);
        }

        return EzJson.fromJson(dataSetValue);
    }

    /**
     * @static
     * @public @method
     * Gets the element's dataset value as an int number for the provided dataSetKey for the element associated with the provided elementRef.
     * Can exclude the 'data-' prefix and/or send the dataSetKey as camel cased instead of with dashes.
     *
     * @param {string} elementRef
     * @param {string} dataSetKey
     *
     * @returns {number}
     */
    static getDatasetValueAsInt(elementRef, dataSetKey) {
        const dataSetValue = EzUx.getDatasetValue(elementRef, dataSetKey);

        if (!EzNumber.isNumber(dataSetValue) && !EzString.hasLength(dataSetValue)) {
            throw new EzBadStateException(
                `Expected a valid number (or string representing a number) from element.dataset[${dataSetKey}]`,
                EzString.em`
                    However, the value element.dataset[${dataSetKey}] is either undefined, null, not a string representing a number, or not a number.
                    [Dataset Value: ${EzObject.isValid(dataSetValue) ? dataSetValue.toString() : 'undefined or null'}]`,
                EzUx,
                EzUx.getDatasetValueAsInt);
        }

        return EzNumber.strToInt(dataSetValue);
    }

    /**
     * @public @method
     * Sets an input's readonly property to true
     *
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetInputReadOnly(elementOrId)
     *
     * @param {string} elementRef
     */
    static setInputReadOnly(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot determine if checkbox ${elementRef} is checked or not: Element does not exist.`);

            return null;
        }

        element.readOnly = true;
    }

    /**
     * @static
     * @public @method
     * Determines if the element or element with the provided id is checked or not.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezIsCheckBoxChecked(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezIsCheckboxChecked(elementOrId)
     *
     * @param {string} elementRef
     *
     * @returns {boolean}
     */
    static isCheckboxChecked(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot determine if checkbox ${elementRef} is checked or not: Element does not exist.`);

            return false;
        }

        return EzBoolean.booleanOrFalse(element.checked);
    }

    /**
     * @static
     * @public @method
     * Sets an HTML checkbox input's checked value
     * Replaces
     *  globalThis.ezApi.ezclocker.ezUi.ezSetCheckboxValue(elementOrId, checked)
     *
     * @param {string} elementRef
     * @param {boolean} checked
     */
    static setCheckboxValue(elementRef, checked) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot set checkbox ${elementRef} value to ${checked}: Element does not exist.`);

            return;
        }

        element.checked = EzBoolean.booleanOrFalse(checked);
    }

    /**
     * @static
     * @public @method
     * Sets an HTML radio input's checked value
     * Replaces
     *  globalThis.ezApi.ezclocker.ezUi.ezSetRadioBoxValue(elementOrId, checked)
     *
     * @param {string} elementRef
     * @param {boolean} checked
     * Default: false
     */
    static ezSetRadioBoxValue(elementRef, checked = false) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot set radio box ${elementRef} value to ${checked}: Element does not exist.`);

            return;
        }

        element.checked = EzBoolean.booleanOrFalse(checked);
    }

    /**
     * @static
     * @public @method
     * Adds the error highlight to an input editor
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezShowInputError
     * @param {string} elementRef
     */
    static showInputError(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot show input error for element ${elementRef}: Element does not exist.`);

            return;
        }

        // Class ezInputErrorHighlight is located in ez-inputs.css
        if (!element.classList.contains('ezInputErrorHighlight')) {
            element.classList.add('ezInputErrorHighlight');
        }
    }

    /**
     * @static
     * @public @method
     * Removes the error highlight to an input editor
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezHideInputError(elementOrId)
     * @param {string} elementRef
     */
    static hideInputError(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot hide input error for element ${elementRef}: Element does not exist.`);

            return;
        }

        // Class ezInputErrorHighlight is located in ez-inputs.css
        if (element.classList.contains('ezInputErrorHighlight')) {
            element.classList.remove('ezInputErrorHighlight');
        }
    }

    /**
     * @static
     * @public @method
     * Sets the value of the select input identified by
     * Replaces
     *  globalThis.ezApi.ezclocker.ezUi.ezSetSelectValue(elementRef, value)
     * @param {string} elementRef
     * @param {*} value
     */
    static setSelectValue(elementRef, value) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot set select input ${elementRef} value to ${value}: Element does not exist.`);

            return;
        }

        if (EzUx.elementExists(elementRef)) {
            EzUx.setInputValue(elementRef, value);
        } else {
            // Assume it is a JQuery UI selector instead, and use the older jquery
            EzUx.setSelectValue$(elementRef, value);
        }
    }

    /**
     * @static
     * @public @method @statc
     * Sets the value of the select input identified by the provided jquery selector
     *
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetSelectValue$(jquerySelector, value)
     *
     * @param {string} jquerySelector
     * @param {*} value
     */
    static setSelectValue$(jquerySelector, value) {
        const jqueryElement = EzUx.$(jquerySelector);

        if (!EzObject.isValid(jqueryElement)) {
            globalThis?.console?.error(`Cannot set jQuery select input ${jquerySelector} value to ${value}: Element does not exist.`);

            return;
        }

        jqueryElement.val(value);

        jqueryElement.selectmenu('refresh');
    }

    /**
     * @static
     * @public @method
     * Removes the element or element with id from the globalThis.document.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezRemoveElement(elementOrId)
     * @param {string} elementRef
     */
    static removeElement(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot remove element ${elementRef}: Element does not exist.`);

            return;
        }

        element.remove();
    }

    /**
     * @static
     * @public @method
     * Selects all the text in an input.
     * Replaces:
     *  1) globalThis.ezApi.ezclocker.ezUi.ezSelectAll(id)
     *  2) globalThis.ezApi.ezclocker.ezUi.ezInputSelectAll(id)
     *  3) globalThis.ezApi.ezclocker.ezUi.ezInputSelectAll$(selector)
     * @param {string} elementRef
     */
    static selectAll(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            globalThis?.console?.error(`Cannot select all in input element ${elementRef}: Element does not exist.`);

            return;
        }

        element.focus();

        element.select();
    }

    /**
     * @static
     * @public @method
     * Sets a css style property on the element or element with the provided id
     *
     * Replaces
     * globalThis.ezApi.ezclocker.ezUi.ezSetElementCss(elementOrId, cssName, cssValue)
     *
     * @param {string} elementRef
     * @param {string} cssPropertyName
     * @param {undefined|null|any} cssPropertyValue
     * Default: null
     */
    static setCssProperty(elementRef, cssPropertyName, cssPropertyValue = null) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            // No element to set the css style property for
            return;
        }

        let nextUpper = false;

        let newCssName = EzString.EMPTY;

        for (const cssPropertyChar of cssPropertyName) {
            if (nextUpper) {
                newCssName = `${newCssName}${cssPropertyChar.toUpperCase()}`;

                nextUpper = false;
            } else if ('-' === cssPropertyChar) {
                nextUpper = true;
            } else {
                newCssName = `${newCssName}${cssPropertyChar}`;
            }
        }

        element.style[newCssName] = cssPropertyValue;
    }

    /**
     * @static
     * @public @method
     * Adds a new class to the element or element with provided id
     *
     * Replacement for the following globalThis.ezApi.ezclocker.ezUi.ezAddClass methods:
     *  1) ezAddElementClass(elementOrId, className)
     *  2) ezAddClass(id, className)
     *  3) ezAddClass$(selector, className)
     *
     * @param {string} elementRef
     * @param {string} className
     *
     * @returns {null|object}
     * Returns the element reference if it exists, null otherwise
     */
    static addCssClass(elementRef, ...classNames) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (!EzArray.hasLength(classNames)) {
            return element;
        }

        for (const className of classNames) {
            if (!element?.classList?.contains(className)) {
                element.classList.add(className);
            }
        }

        return element;
    }

    /**
     * @static
     * @public @method
     * Removes a CSS class from the element associatd with elementRef
     *
     * Replacement for the following globalThis.ezApi.ezclocker.ezUi.ezAddClass methods:
     *  1) ezRemoveElementClass(elementOrId, className)
     *
     * @param {string} elementRef
     * @param {string} className
     *
     * @returns {null|object}
     * Returns the element reference if it exists, null otherwise
     */
    static removeCssClass(elementRef, className) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (!EzString.hasLength(className)) {
            return element;
        }

        if (element?.classList?.contains(className)) {
            element.classList?.remove(className);
        }

        return element;
    }

/**
     * @public @method
     * Obtains a data attribute from the element or element associated with the provided id.
     * No need to include the 'data-' portion of the attribute. If included, it is removed before accessing the
     * elements dataset.
     * Replaces:
     * 1) globalThis.ezApi.ezUi.ezGetElementDataAttributeValue(elementOrId, dataAttributeName)
     * @param {object|string} elementRef
     * @param {string} dataAttributeName
     * @returns {string}
     */
    static getElementDataAttributeValue(elementRef, dataAttributeName) {
        if (!EzString.hasLength(dataAttributeName)) {
            throw new EzBadParamException(
                'dataAttributeName',
                EzUx.ezInstance,
                EzUx.ezInstance.ezGetElementDataAttributeValue);
        }

        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (0 === dataAttributeName.indexOf('data-')) {
            dataAttributeName = dataAttributeName.substring(EzNumber.FIVE);
        }

        dataAttributeName = dataAttributeName.replace('-', EzString.EMPTY);

        if (!EzObject.hasProperty(element.dataset, dataAttributeName)) {
            return EzString.EMPTY;
        }

        const value = element.dataset[dataAttributeName];

        return EzString.stringWithTextOrEmpty(value);
    }

    /**
     * @public @method
     * Sets a data attribute on the element or element associated with the provided id.
     * No need to include the 'data-' portion of the attribute. If included, it is removed before accessing the
     * elements dataset.
     * Replaces:
     * 1) globalThis.ezApi.ezclocker.ezUi.ezSetElementDataAttributeValue(elementOrId, dataAttributeName, dataAttributeValue)
     * @param {object|string} elementRef
     * @param {string} dataAttributeName
     * @param {string|null} dataAttributeValue
     */
    setElementDataAttribute(elementRef, dataAttributeName, dataAttributeValue) {
        if (!EzString.hasText(dataAttributeName)) {
            throw new EzBadParamException(
                'dataAttributeName',
                EzUx.ezInstance,
                EzUx.ezInstance.ezSetElementDataAttributeValue);
        }

        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (0 === dataAttributeName.indexOf('data-')) {
            dataAttributeName = dataAttributeName.substring(EzNumber.FIVE);
        }

        const nameParts = dataAttributeName.split('-');

        let realDatAttributeName = EzString.EMPTY;

        // Converting to camal case
        for (let index = EzNumber.FIRST_INDEX; index < nameParts.length; index++) {
            if (0 === index) {
                realDatAttributeName += index;
            } else {
                realDatAttributeName += `${nameParts[index].charAt(EzNumber.FIRST_INDEX).toUpperCase()}${nameParts[index].substring(EzNumber.ONE)}`;
            }
        }

        element.dataset[realDatAttributeName] = EzString.stringWithTextOrEmpty(dataAttributeValue);
    }

    /**
     * @static
     * @public @method
     * Sets an attribute on the element identified by id
     *
     * Replaces:
     * globalThis.ezApi.ezclocker.ezUi.ezSetElementAttribute(id, attributeName, attributeValue)
     *
     * @param {string} id
     * @param {string} attributeName
     * @param {undefined|null|string} attributeValue
     * Default: EzString.EMPTY
     */
    static setElementAttribute(elementRef, attributeName, attributeValue = EzString.EMPTY) {
        if (!EzString.hasLength(attributeName)) {
            globalThis?.console?.error('Cannot set an element attribute with a undefined, null, or blank attributeName');

            return;
        }

        const element = EzUx.getElementForElementRef(
            elementRef,
            'setElementAttribute',
            true);

        element[attributeName] = EzString.stringOrEmpty(attributeValue);

        // Original
        // EzUI.ezInstance.ezId$(selector).attr(attributeName, attributeValue);
    }

    /**
     * @static
     * @public @method
     * Builds an element id from the provided entity id and prefix
     *
     * Replaces:
     * globalThis.ezApi.ezclocker.ezUi.ezBuildEntityElementId(entityId, idPrefix, idSuffix)
     *
     * @param {string|number} entityId
     * @param {undefined|null|string} idPrefix
     * @param {undefined|null|string} idSuffix
     */
    buildEntityElementId(entityId, idPrefix = 'EzElement', idSuffix = EzString.EMPTY) {
        if (!EzNumber.isNumber(entityId) && !EzString.hasLength(entityId)) {
            throw new EzBadParamException(
                'entityId',
                EzUx,
                EzUx.buildEntityElementId);
        }

        const DEFAULT_ID_PREFIX = 'EzElement';

        return EzNumber.isNumber(entityId)
            ? EzUx.buildId`
                ${EzString.stringWithTextOrDefault(idPrefix, DEFAULT_ID_PREFIX)}_
                ${EzNumber.asString(entityId)}_
                ${EzString.stringWithTextOrDefault(idSuffix, EzString.EMPTY)}`
            : EzUx.buildId`
                ${EzString.stringWithTextOrDefault(idPrefix, DEFAULT_ID_PREFIX)}_
                ${EzString.stringWithLengthOrDefault(entityId, 'EntityId')}_
                ${EzString.stringWithTextOrDefault(idSuffix, EzString.EMPTY)}`;
    }

    /**
     * @public @method
     * Removes all spaces and line feeds from the provided aTemplateLitteralValue param. Then returns
     * the trimmed value.
     * Result template: '${value_with_no_space_linefeeds}';
     *
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    buildId(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzArray.hasLength(aStrings)) {
            for (let x = 0; x < aStrings.length; x++) {
                let aString = aStrings[x];

                if (EzNumber.isNumber(aString) || EzBoolean.isBoolean(aString)) {
                    aString = aString.toString();
                }

                aString = EzString.hasLength(aString)
                    ? aString.trim()
                    : EzString.EMPTY;

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzNumber.isNumber(aKey) || EzBoolean.isBoolean(aKey)
                    ? aKey.toString()
                    : EzString.stringOrEmpty(aKey);

                const cleanValue = aString.trimStart().replace(/[ \n\r\t]+/g, EzString.EMPTY);

                const cleanLine = `${cleanValue}${aKey}`.trim();

                cleanValues = `${cleanValues}${cleanLine}`.trim();
            }
        }

        return `${cleanValues}`.trim();
    }

    /**
     * @public @method
     * Sets the position of the scroll bar element associated with the provided id
     * Replaces:
     *  globalThis.ezApi.ezUi.ezScrollTo(elementOrId, x, y)
     * @param {object|string}} elementRef
     * @param {number} x
     * @param {number} y
     */
    static scrollTo(elementRef, x, y) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (EzNumber.isNumber(x)) {
            element.scrollTop = x;
        }

        if (EzNumber.isNumber(y)) {
            element.scrollLeft = y;
        }
    }

    /**
     * @public @method
     * Sets the position of the scroll bars associated with the provided id to top left
     * Replaces:
     *  globalThis.ezApi.ezUi.ezScrollToTop(elementRef) {
     * @param {object|string} elementRef
     */
    static scrollToTop(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        element.scrollTop = 0;

        element.scrollLeft = 0;
    }


    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @public @constructor
     * DOES NOT CREATE A NEW INSTANCE
     * Throws exception, only class static method and properties.
     */
    constructor() {
        throw new EzException(
            'Do not create a new instance of EzUx. Access via static method and properties only.');
    }

    /**
     * @static
     * @private @method
     * Handles the document's KEY_UP event to show access key underlins.
     */
    static #ezHandleDocumentKeyDownEvent_ShowAccessKeys(event) {
        if (EzBoolean.isTrue(event.altKey)) {
            const elementsWithAccessKeysOff = globalThis.document.getElementsByClassName(EzUx.css_ezTextUnerlineHideAltKeyUp);

            for (const element of elementsWithAccessKeysOff) {
                globalThis.ezApi.ezclocker.ezUi.ezRemoveElementClass(
                    element,
                    EzUx.css_ezTextUnerlineHideAltKeyUp);

                globalThis.ezApi.ezclocker.ezUi.ezAddElementClass(
                    element,
                    EzUx.css_ezTextUnderlineShowAltKeyDown);
            }
        }
    }

    /**
     * @static
     * @private @method
     * Handles the document's KEY_UP event to hide access key underlins.
     */
    static #ezHandleDocumentKeyUpEvent_HideAccessKeys(event) {
        if (EzBoolean.isFalse(event.altKey)) {
            const elementsWithAccessKeysOn = globalThis?.document?.getElementsByClassName(EzUx.css_ezTextUnderlineShowAltKeyDown);

            for (const element of elementsWithAccessKeysOn) {
                globalThis.ezApi.ezclocker.ezUi.ezRemoveElementClass(
                    element,
                    EzUx.css_ezTextUnderlineShowAltKeyDown);

                globalThis.ezApi.ezclocker.ezUi.ezAddElementClass(
                    element,
                    EzUx.css_ezTextUnerlineHideAltKeyUp);
            }
        }
    }

    /**
     * @static
     * @private @field
     * Stores if the access key show/hide events have been hooked for the document
     * @type {boolean}
     */
    static #ezHookedAccessKeyShowHideEvents = false;

    /**
     * @static @proviate @field
     * If this.#ezHookedAccessKeyShowHideEvents is false, then hooks the documents
     * KEY_DOWN and KEY_UP event in order to show/hide access keys.
     */
    static #ezHookAccessKeyShowHideEvents() {
        if (EzBoolean.booleanOrFalse(this.#ezHookedAccessKeyShowHideEvents)) {
            return;
        }

        this.#ezHookedAccessKeyShowHideEvents = true;

        $(globalThis?.document).ready(
            () => {
                if (globalThis?.window) {
                    // Show access keys on ALT key down
                    globalThis.window.addEventListener(
                        'keydown',
                        this.#ezHandleDocumentKeyDownEvent_ShowAccessKeys);
                    globalThis.window.addEventListener(
                        'keyup',
                        this.#ezHandleDocumentKeyUpEvent_HideAccessKeys);
                }
            });
    }

    /**
     * @static
     * Static initialization of EzUx class.
     */
    static {
        $(document).ready(
            () => this.#ezHookAccessKeyShowHideEvents());
    }


    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}

globalThis.EzUx = EzUx;

/**
 * Alternative name for EzUx
 * Provided to try and avoid issues with typos in the name
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzUX } from '/ezlibrary/ux/EzUx.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
globalThis.window.EzUx = EzUx;
